import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ERPLayout from "@/components/feature/ERPLayout";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import { clientsApi, quotationsApi } from "@/services/api";

interface LineItem {
  id: number;
  description: string;
  qty: number;
  rate: number;
  gstRate: number;
}

interface ServiceType {
  id: number;
  name: string;
  description?: string;
  base_rate: number;
  unit: string;
  is_active: number;
}

interface QuotationTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  bg: string;
  serviceNames: string[];
  notes: string;
  terms: string;
}

const QUOTATION_TEMPLATES: QuotationTemplate[] = [
  {
    id: "topo-survey",
    name: "Topographic Survey",
    description: "Full topo survey with CAD drafting and report",
    icon: "ri-map-2-line",
    color: "#10b981",
    bg: "#ecfdf5",
    serviceNames: ["Topographic Survey", "CAD Drafting", "Report Preparation & Submission"],
    notes: "Thank you for your business. Payment is due within 30 days of invoice date.",
    terms: "50% advance payment required before commencement of work. Balance on delivery of final report.",
  },
  {
    id: "dgps-survey",
    name: "DGPS Survey",
    description: "DGPS survey with KMZ and Excel export",
    icon: "ri-radar-line",
    color: "#7c3aed",
    bg: "#f5f3ff",
    serviceNames: ["DGPS Survey", "KMZ Export", "Excel Export"],
    notes: "Thank you for your business. Payment is due within 30 days of invoice date.",
    terms: "Full payment required before delivery of digital data files.",
  },
  {
    id: "drone-survey",
    name: "Drone Survey Package",
    description: "Drone survey with CAD and full report",
    icon: "ri-flight-takeoff-line",
    color: "#f59e0b",
    bg: "#fffbeb",
    serviceNames: ["Drone Survey", "CAD Drafting", "Report Preparation & Submission"],
    notes: "Drone survey subject to weather conditions and local authority permissions.",
    terms: "50% advance required. Balance payable within 15 days of report delivery.",
  },
  {
    id: "soil-test",
    name: "Soil Testing",
    description: "Soil test with lab analysis and report",
    icon: "ri-flask-line",
    color: "#ef4444",
    bg: "#fef2f2",
    serviceNames: ["Soil Test", "Report Preparation & Submission"],
    notes: "Soil samples will be collected from site and tested at our certified laboratory.",
    terms: "Payment due within 15 days of report submission.",
  },
  {
    id: "boundary-survey",
    name: "Boundary Survey",
    description: "Boundary demarcation with revenue survey",
    icon: "ri-map-pin-line",
    color: "#3b82f6",
    bg: "#eff6ff",
    serviceNames: ["Boundary Survey", "Revenue Survey", "CAD Drafting"],
    notes: "Boundary survey will be conducted as per revenue records and field verification.",
    terms: "50% advance required. Balance on completion of field work.",
  },
  {
    id: "hydro-survey",
    name: "Hydrogeological Survey",
    description: "Hydro survey with full reporting",
    icon: "ri-water-flash-line",
    color: "#0ea5e9",
    bg: "#f0f9ff",
    serviceNames: ["Hydrogeological Survey", "Report Preparation & Submission"],
    notes: "Hydrogeological survey includes groundwater assessment and aquifer mapping.",
    terms: "30% advance, 40% on field completion, 30% on report delivery.",
  },
];

interface ClientOption {
  id: number;
  name: string;
  company?: string;
  gstin?: string;
  address?: string;
  email?: string;
  phone?: string;
}

const GST_RATES = [0, 5, 12, 18, 28];

function numToWords(n: number): string {
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  if (n === 0) return "Zero";
  if (n < 20) return ones[n];
  if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
  if (n < 1000) return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + numToWords(n % 100) : "");
  if (n < 100000) return numToWords(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + numToWords(n % 1000) : "");
  if (n < 10000000) return numToWords(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + numToWords(n % 100000) : "");
  return numToWords(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 ? " " + numToWords(n % 10000000) : "");
}

export default function NewQuotationPage() {
  const navigate = useNavigate();
  const { settings } = useCompanySettings();
  const printRef = useRef<HTMLDivElement>(null);

  // Live data
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [servicesLoading, setServicesLoading] = useState(true);

  const [quotationDate, setQuotationDate] = useState(new Date().toISOString().split("T")[0]);
  const [validUntil, setValidUntil] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return d.toISOString().split("T")[0];
  });
  const [selectedClientId, setSelectedClientId] = useState<number>(0);
  const [jobRef, setJobRef] = useState("");
  const [notes, setNotes] = useState("Thank you for your business. Payment is due within 30 days of invoice date.");
  const [terms, setTerms] = useState("50% advance payment required before commencement of work. Balance on delivery of final report.");
  const [showPreview, setShowPreview] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [appliedTemplate, setAppliedTemplate] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedNo, setSavedNo] = useState("");
  const [saveError, setSaveError] = useState("");

  const [items, setItems] = useState<LineItem[]>([
    { id: 1, description: "Survey Field Work", qty: 1, rate: 25000, gstRate: 18 },
    { id: 2, description: "CAD Drafting & Drawing Preparation", qty: 1, rate: 15000, gstRate: 18 },
    { id: 3, description: "Report Preparation & Submission", qty: 1, rate: 8000, gstRate: 18 },
  ]);

  // Load clients and service types from API
  useEffect(() => {
    const load = async () => {
      setDataLoading(true);
      setServicesLoading(true);
      try {
        const [clientRes, servicesRes] = await Promise.all([
          clientsApi.getAll({ limit: 200 }),
          fetch('/api/service-types').then(r => r.json()),
        ]);
        const clientList: ClientOption[] = (clientRes.data || clientRes || []) as ClientOption[];
        const servicesList: ServiceType[] = Array.isArray(servicesRes) 
          ? servicesRes 
          : (servicesRes.data || []);
        
        setClients(clientList);
        setServiceTypes(servicesList.filter((s: ServiceType) => s.is_active !== 0));
        
        if (clientList.length) setSelectedClientId(clientList[0].id);
        
        // Initialize items with first 3 services if available
        if (servicesList.length > 0 && items.length === 3 && items[0].description === "Survey Field Work") {
          const defaultItems = servicesList.slice(0, 3).map((svc, idx) => ({
            id: Date.now() + idx,
            description: svc.name,
            qty: 1,
            rate: Number(svc.base_rate) || 0,
            gstRate: 18,
          }));
          setItems(defaultItems);
        }
      } catch {
        setClients([]);
        setServiceTypes([]);
      } finally {
        setDataLoading(false);
        setServicesLoading(false);
      }
    };
    load();
  }, []);

  const selectedClient = clients.find((c) => c.id === selectedClientId) || clients[0];

  const addItem = () => {
    const defaultService = serviceTypes[0];
    setItems((prev) => [...prev, { 
      id: Date.now(), 
      description: defaultService?.name || "", 
      qty: 1, 
      rate: Number(defaultService?.base_rate) || 0, 
      gstRate: 18 
    }]);
  };

  const removeItem = (id: number) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const updateItem = (id: number, field: keyof LineItem, value: string | number) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, [field]: value } : i)));
  };

  const applyTemplate = (template: QuotationTemplate) => {
    if (serviceTypes.length === 0) return;
    const newItems: LineItem[] = template.serviceNames.map((name, idx) => {
      const svc = serviceTypes.find(
        (s) => s.name.toLowerCase().includes(name.toLowerCase()) || name.toLowerCase().includes(s.name.toLowerCase())
      );
      return {
        id: Date.now() + idx,
        description: svc ? svc.name : name,
        qty: 1,
        rate: svc ? Number(svc.base_rate) || 0 : 0,
        gstRate: 18,
      };
    });
    setItems(newItems);
    setNotes(template.notes);
    setTerms(template.terms);
    setAppliedTemplate(template.id);
    setShowTemplates(false);
  };

  const subtotal = items.reduce((acc, i) => acc + i.qty * i.rate, 0);

  const gstBreakdown = items.reduce((acc, item) => {
    const taxable = item.qty * item.rate;
    const gstAmt = (taxable * item.gstRate) / 100;
    const key = item.gstRate;
    if (!acc[key]) acc[key] = { taxable: 0, cgst: 0, sgst: 0, total: 0 };
    acc[key].taxable += taxable;
    acc[key].cgst += gstAmt / 2;
    acc[key].sgst += gstAmt / 2;
    acc[key].total += gstAmt;
    return acc;
  }, {} as Record<number, { taxable: number; cgst: number; sgst: number; total: number }>);

  const totalGST = Object.values(gstBreakdown).reduce((a, b) => a + b.total, 0);
  const grandTotal = subtotal + totalGST;

  const handlePrint = () => {
    navigate("/billing/quotation/print");
  };

  const handleSave = async (sendForApproval = false) => {
    if (!selectedClient?.id) {
      setSaveError("Please select a client first");
      setTimeout(() => setSaveError(""), 4000);
      return;
    }

    if (items.length === 0 || items.every(i => !i.description.trim())) {
      setSaveError("Please add at least one item");
      setTimeout(() => setSaveError(""), 4000);
      return;
    }

    setSaving(true);
    setSaveError("");
    try {
      const payload = {
        client_id: selectedClient.id,
        valid_until: validUntil,
        job_ref: jobRef,
        notes,
        terms,
        items: items
          .filter(i => i.description.trim())
          .map((i) => ({
            description: i.description,
            qty: i.qty,
            rate: i.rate,
            tax_pct: i.gstRate,
            unit: "",
          })),
        status: sendForApproval ? "pending_approval" : "draft",
      };

      const result = await quotationsApi.create(payload as unknown as Record<string, unknown>);
      const no = result.doc_no || result.quotation_no || "QT-DRAFT";
      setSavedNo(sendForApproval ? `Sent for approval: ${no}` : `Saved as draft: ${no}`);
      setTimeout(() => setSavedNo(""), 4000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setSaveError(`Error: ${errorMsg}`);
      setTimeout(() => setSaveError(""), 5000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ERPLayout title="New Quotation">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/billing")} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 cursor-pointer">
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">New Quotation</h1>
              <p className="text-sm text-gray-500">Auto-generated number on save</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {savedNo && (
              <span className="flex items-center gap-1.5 text-sm text-amber-600 font-medium">
                <i className="ri-checkbox-circle-line" /> {savedNo}
              </span>
            )}
            {saveError && (
              <span className="text-sm text-red-500">{saveError}</span>
            )}
            <button onClick={() => setShowPreview(true)} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-eye-line" /></span>Preview
            </button>
            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={saving ? "ri-loader-4-line animate-spin" : "ri-save-line"} />
              </span>
              {saving ? "Saving..." : "Save Quotation"}
            </button>
          </div>
        </div>

        {dataLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
            <i className="ri-loader-4-line animate-spin text-amber-500" />
            Loading clients and services from server...
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          {/* Left: Main Form */}
          <div className="col-span-2 space-y-5">
            {/* Quotation Meta */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="font-semibold text-sm text-gray-800 mb-4">Quotation Details</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1.5 block">Quotation Date</label>
                  <input type="date" value={quotationDate} onChange={(e) => setQuotationDate(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1.5 block">Valid Until</label>
                  <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1.5 block">Job Reference</label>
                  <input type="text" value={jobRef} onChange={(e) => setJobRef(e.target.value)} placeholder="e.g. HYD-SURV-2026-0034"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
                </div>
              </div>
            </div>

            {/* Client */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="font-semibold text-sm text-gray-800 mb-4">Bill To</h3>
              <div className="mb-4">
                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Client</label>
                <select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(Number(e.target.value))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                >
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</option>)}
                </select>
              </div>
              {selectedClient && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-500">GSTIN</p>
                    <p className="font-medium text-gray-800">{selectedClient.gstin || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="font-medium text-gray-800">{selectedClient.email || "—"}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500">Address</p>
                    <p className="font-medium text-gray-800">{selectedClient.address || "—"}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Templates */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <button
                onClick={() => setShowTemplates(!showTemplates)}
                className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50">
                    <i className="ri-layout-grid-line text-amber-600 text-sm" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-800">
                      Quotation Templates
                      {appliedTemplate && (
                        <span className="ml-2 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                          {QUOTATION_TEMPLATES.find(t => t.id === appliedTemplate)?.name} applied
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-400">Quick-start with a pre-built service combination</p>
                  </div>
                </div>
                <i className={`ri-arrow-down-s-line text-gray-400 text-lg transition-transform ${showTemplates ? "rotate-180" : ""}`} />
              </button>

              {showTemplates && (
                <div className="px-5 pb-5 border-t border-gray-100">
                  <p className="text-xs text-gray-400 mt-3 mb-3">
                    Select a template to auto-fill line items, notes, and terms. You can edit everything after applying.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {QUOTATION_TEMPLATES.map((tpl) => (
                      <button
                        key={tpl.id}
                        onClick={() => applyTemplate(tpl)}
                        disabled={serviceTypes.length === 0 || servicesLoading}
                        className={`text-left p-4 rounded-xl border-2 transition-all cursor-pointer hover:border-amber-400 hover:bg-amber-50/30 disabled:opacity-50 disabled:cursor-not-allowed ${
                          appliedTemplate === tpl.id ? "border-amber-400 bg-amber-50" : "border-gray-100"
                        }`}
                      >
                        <div className="flex items-center gap-2.5 mb-2">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: tpl.bg }}>
                            <i className={`${tpl.icon} text-sm`} style={{ color: tpl.color }} />
                          </div>
                          <p className="text-sm font-semibold text-gray-800 leading-tight">{tpl.name}</p>
                        </div>
                        <p className="text-xs text-gray-500 mb-2">{tpl.description}</p>
                        <div className="flex flex-wrap gap-1">
                          {tpl.serviceNames.slice(0, 3).map((sn) => (
                            <span key={sn} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium">
                              {sn.length > 18 ? sn.slice(0, 18) + "..." : sn}
                            </span>
                          ))}
                        </div>
                        {appliedTemplate === tpl.id && (
                          <div className="mt-2 flex items-center gap-1 text-amber-600">
                            <i className="ri-checkbox-circle-fill text-xs" />
                            <span className="text-xs font-semibold">Applied</span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Line Items */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-sm text-gray-800">Line Items</h3>
                <button onClick={addItem} className="flex items-center gap-1.5 text-xs text-amber-600 font-medium hover:text-amber-700 cursor-pointer whitespace-nowrap">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-circle-line" /></span>Add Item
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left pb-2 text-xs font-semibold text-gray-500 w-1/2">Description</th>
                      <th className="text-center pb-2 text-xs font-semibold text-gray-500 w-16">Qty</th>
                      <th className="text-right pb-2 text-xs font-semibold text-gray-500 w-28">Rate (₹)</th>
                      <th className="text-center pb-2 text-xs font-semibold text-gray-500 w-20">GST %</th>
                      <th className="text-right pb-2 text-xs font-semibold text-gray-500 w-28">Amount (₹)</th>
                      <th className="w-8" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {items.map((item) => (
                      <tr key={item.id}>
                        <td className="py-2 pr-2">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateItem(item.id, "description", e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                            placeholder="Service description"
                          />
                        </td>
                        <td className="py-2 px-1">
                          <input
                            type="number"
                            min={1}
                            value={item.qty}
                            onChange={(e) => updateItem(item.id, "qty", Number(e.target.value))}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                          />
                        </td>
                        <td className="py-2 px-1">
                          <input
                            type="number"
                            min={0}
                            value={item.rate}
                            onChange={(e) => updateItem(item.id, "rate", Number(e.target.value))}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                          />
                        </td>
                        <td className="py-2 px-1">
                          <select
                            value={item.gstRate}
                            onChange={(e) => updateItem(item.id, "gstRate", Number(e.target.value))}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                          >
                            {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                          </select>
                        </td>
                        <td className="py-2 pl-1 text-right font-semibold text-gray-800">
                          ₹{(item.qty * item.rate).toLocaleString()}
                        </td>
                        <td className="py-2 pl-1">
                          <button onClick={() => removeItem(item.id)} className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-500 cursor-pointer">
                            <i className="ri-close-line text-sm" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* GST Breakdown */}
              <div className="mt-5 border-t border-gray-100 pt-4">
                <h4 className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wide">GST Breakdown</h4>
                <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left px-4 py-2 text-gray-500 font-semibold">GST Rate</th>
                        <th className="text-right px-4 py-2 text-gray-500 font-semibold">Taxable Amt</th>
                        <th className="text-right px-4 py-2 text-gray-500 font-semibold">CGST</th>
                        <th className="text-right px-4 py-2 text-gray-500 font-semibold">SGST</th>
                        <th className="text-right px-4 py-2 text-gray-500 font-semibold">Total GST</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(gstBreakdown).map(([rate, vals]) => (
                        <tr key={rate} className="border-b border-gray-100 last:border-0">
                          <td className="px-4 py-2 font-medium text-gray-700">{rate}%</td>
                          <td className="px-4 py-2 text-right text-gray-700">₹{vals.taxable.toLocaleString()}</td>
                          <td className="px-4 py-2 text-right text-gray-700">₹{vals.cgst.toLocaleString()}</td>
                          <td className="px-4 py-2 text-right text-gray-700">₹{vals.sgst.toLocaleString()}</td>
                          <td className="px-4 py-2 text-right font-semibold text-gray-800">₹{vals.total.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="mt-4 flex justify-end">
                <div className="w-72 space-y-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Subtotal</span>
                    <span className="font-medium">₹{subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Total GST</span>
                    <span className="font-medium">₹{totalGST.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold text-gray-900 border-t border-gray-200 pt-2">
                    <span>Grand Total</span>
                    <span>₹{grandTotal.toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-gray-500 italic">
                    {numToWords(Math.round(grandTotal))} Rupees Only
                  </p>
                </div>
              </div>
            </div>

            {/* Notes & Terms */}
            <div className="bg-white rounded-xl border border-gray-100 p-5 grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Notes</label>
                <textarea rows={3} maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 resize-none" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Terms &amp; Conditions</label>
                <textarea rows={3} maxLength={500} value={terms} onChange={(e) => setTerms(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 resize-none" />
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="col-span-1 space-y-4">
            {/* Summary */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="font-semibold text-sm text-gray-800 mb-4">Quotation Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-semibold text-gray-800">₹{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">GST</span>
                  <span className="font-semibold text-gray-800">₹{totalGST.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm font-bold text-gray-900 border-t border-gray-100 pt-2">
                  <span>Grand Total</span>
                  <span>₹{grandTotal.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="font-semibold text-sm text-gray-800 mb-3">Quotation Status</h3>
              <div className="space-y-2">
                {[
                  { label: "Draft", done: true },
                  { label: "Sent to Client", done: false },
                  { label: "Client Approved", done: false },
                  { label: "Converted to Job", done: false },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center ${s.done ? "bg-amber-500" : "bg-gray-200"}`}>
                      {s.done && <i className="ri-check-line text-white text-xs" />}
                    </span>
                    <span className={`text-xs ${s.done ? "text-gray-800 font-medium" : "text-gray-400"}`}>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-semibold hover:bg-amber-700 cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={saving ? "ri-loader-4-line animate-spin" : "ri-save-line"} />
                </span>
                {saving ? "Saving..." : "Save Draft"}
              </button>
              <button onClick={() => handleSave(true)} disabled={saving}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 cursor-pointer whitespace-nowrap disabled:opacity-60">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line" /></span>Send for Approval
              </button>
              <button onClick={() => setShowPreview(true)} className="w-full flex items-center justify-center gap-2 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 cursor-pointer whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-eye-line" /></span>Preview PDF
              </button>
              <button onClick={handlePrint} className="w-full flex items-center justify-center gap-2 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 cursor-pointer whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line" /></span>Print / Export PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Print Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-3xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">Quotation Preview</h3>
              <div className="flex items-center gap-2">
                <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 cursor-pointer whitespace-nowrap">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line" /></span>Print / PDF
                </button>
                <button onClick={() => setShowPreview(false)} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer">
                  <i className="ri-close-line text-lg" />
                </button>
              </div>
            </div>
            <div ref={printRef} className="p-8 space-y-6">
              {/* Header */}
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <img src={settings.logo} alt="Logo" className="w-7 h-7 object-contain" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-lg">{settings.name}</p>
                      <p className="text-xs text-gray-500">GSTIN: {settings.gstin}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 max-w-xs">{settings.address}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-amber-600">QUOTATION</p>
                  <p className="text-xs text-gray-500">Date: {quotationDate}</p>
                  <p className="text-xs text-gray-500">Valid Until: {validUntil}</p>
                </div>
              </div>

              {/* Bill To */}
              <div className="grid grid-cols-2 gap-6 bg-gray-50 rounded-xl p-4 border border-gray-100">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Bill To</p>
                  <p className="font-bold text-gray-900">{selectedClient?.name || "—"}</p>
                  <p className="text-xs text-gray-600 mt-0.5">GSTIN: {selectedClient?.gstin || "—"}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{selectedClient?.address || "—"}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Job Reference</p>
                  <p className="font-bold text-amber-600 text-sm">{jobRef || "—"}</p>
                </div>
              </div>

              {/* Items Table */}
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-800 text-white">
                    <th className="text-left px-3 py-2 text-xs font-semibold rounded-tl-lg">#</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold">Description</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold">Qty</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold">Rate</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold">GST%</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold rounded-tr-lg">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={item.id} className="border-b border-gray-100">
                      <td className="px-3 py-2 text-gray-500 text-xs">{idx + 1}</td>
                      <td className="px-3 py-2 text-gray-800">{item.description}</td>
                      <td className="px-3 py-2 text-center text-gray-700">{item.qty}</td>
                      <td className="px-3 py-2 text-right text-gray-700">₹{item.rate.toLocaleString()}</td>
                      <td className="px-3 py-2 text-center text-gray-700">{item.gstRate}%</td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900">₹{(item.qty * item.rate).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* GST + Totals */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-semibold text-gray-600 uppercase mb-2">GST Summary</p>
                  <table className="w-full text-xs border border-gray-100 rounded-xl overflow-hidden">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-1.5 text-gray-500">Rate</th>
                        <th className="text-right px-3 py-1.5 text-gray-500">CGST</th>
                        <th className="text-right px-3 py-1.5 text-gray-500">SGST</th>
                        <th className="text-right px-3 py-1.5 text-gray-500">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(gstBreakdown).map(([rate, vals]) => (
                        <tr key={rate} className="border-t border-gray-100">
                          <td className="px-3 py-1.5 font-medium">{rate}%</td>
                          <td className="px-3 py-1.5 text-right">₹{vals.cgst.toLocaleString()}</td>
                          <td className="px-3 py-1.5 text-right">₹{vals.sgst.toLocaleString()}</td>
                          <td className="px-3 py-1.5 text-right font-semibold">₹{vals.total.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-2">
                  {[
                    { label: "Subtotal", value: `₹${subtotal.toLocaleString()}` },
                    { label: "Total GST", value: `₹${totalGST.toLocaleString()}` },
                    { label: "Grand Total", value: `₹${grandTotal.toLocaleString()}`, bold: true },
                  ].map((row) => (
                    <div key={row.label} className={`flex justify-between text-sm ${row.bold ? "font-bold border-t border-gray-200 pt-2" : ""}`}>
                      <span className="text-gray-600">{row.label}</span>
                      <span className="text-gray-900">{row.value}</span>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500 italic pt-1">{numToWords(Math.round(grandTotal))} Rupees Only</p>
                </div>
              </div>

              {/* Notes */}
              {notes && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-xs font-semibold text-gray-600 mb-1">Notes</p>
                  <p className="text-xs text-gray-600">{notes}</p>
                </div>
              )}

              {/* Terms */}
              {terms && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-xs font-semibold text-gray-600 mb-1">Terms &amp; Conditions</p>
                  <p className="text-xs text-gray-600 whitespace-pre-line">{terms}</p>
                </div>
              )}

              {/* Signature */}
              <div className="flex justify-end pt-4 border-t border-gray-100">
                <div className="text-center">
                  <div className="w-40 h-12 border-b border-gray-300 mb-1" />
                  <p className="text-xs font-semibold text-gray-700">Authorised Signatory</p>
                  <p className="text-xs text-gray-500">{settings.name}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </ERPLayout>
  );
}
